'use client';

import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useEnvironmentStore } from '@/lib/environments/store';
import { EnvironmentCard } from './environment-card';
import { EnvironmentSettings } from './environment-settings';
import { Plus, Search, LayoutGrid, List, Server } from 'lucide-react';

type ViewMode = 'grid' | 'list';

export function EnvironmentList() {
  const {
    environments,
    environmentsLoading,
    selectedEnvironment,
    loadEnvironments,
    setSelectedEnvironment,
  } = useEnvironmentStore();

  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  useEffect(() => {
    loadEnvironments();
  }, [loadEnvironments]);

  const filteredEnvironments = environments.filter((env) => {
    if (typeFilter !== 'all' && env.type !== typeFilter) return false;
    if (search && !env.name.toLowerCase().includes(search.toLowerCase()) &&
        !env.display_name?.toLowerCase().includes(search.toLowerCase())) {
      return false;
    }
    return true;
  });

  if (environmentsLoading && environments.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-40" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Server className="size-6" />
            Environments
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage deployment environments for your agents
          </p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="size-4 mr-2" />
          New Environment
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <Input
            placeholder="Search environments..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="development">Development</SelectItem>
            <SelectItem value="staging">Staging</SelectItem>
            <SelectItem value="production">Production</SelectItem>
            <SelectItem value="custom">Custom</SelectItem>
          </SelectContent>
        </Select>

        <div className="flex items-center border rounded-md">
          <Button
            variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
            size="icon"
            className="rounded-r-none"
            onClick={() => setViewMode('grid')}
          >
            <LayoutGrid className="size-4" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'secondary' : 'ghost'}
            size="icon"
            className="rounded-l-none"
            onClick={() => setViewMode('list')}
          >
            <List className="size-4" />
          </Button>
        </div>
      </div>

      {/* Environment Grid/List */}
      {filteredEnvironments.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Server className="size-12 mx-auto mb-4 opacity-50" />
          <p>No environments found</p>
          {environments.length === 0 ? (
            <p className="text-sm">Create your first environment to get started</p>
          ) : (
            <p className="text-sm">Try adjusting your filters</p>
          )}
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredEnvironments.map((env) => (
            <EnvironmentCard
              key={env.id}
              environment={env}
              onClick={() => setSelectedEnvironment(env)}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {filteredEnvironments.map((env) => (
            <EnvironmentCard
              key={env.id}
              environment={env}
              variant="list"
              onClick={() => setSelectedEnvironment(env)}
            />
          ))}
        </div>
      )}

      {/* Settings Panel (Drawer/Sheet would be better but keeping it simple) */}
      {selectedEnvironment && (
        <EnvironmentSettings
          environment={selectedEnvironment}
          open={true}
          onClose={() => setSelectedEnvironment(null)}
        />
      )}

      {/* Create Dialog */}
      {createDialogOpen && (
        <EnvironmentSettings
          environment={null}
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
        />
      )}
    </div>
  );
}
